import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Col, Form, Image, message, Row, Space, Tabs, Typography } from 'antd';

import { SurgeryEnquiry, useEnquiryEmailSubmission, useSurgeryEnquirySubmission } from 'all-in-one-shared-api';
import { useKeycloak } from 'all-in-one-react-keycloak';
import { PrimaryButton } from 'components';
import { EnquiryInformationCard, SupplementaryDocumentCard } from './card';
import NewEnquiryIcon from 'assets/images/home/new-claim.svg';
import { SurgeryEnquiryFinishResult } from './SurgeryEnquiryFinishResult';
import { uploadFiles } from 'api/uploadEnquiryDocument';
import { Attachments } from 'types';
import { MessageType } from 'models/MessageType';
import { revalidateAntdFormLanguageChanged } from '../../../i18n';
import { RcFile } from 'antd/lib/upload';

const { Title, Text } = Typography;
const { Item: FormItem } = Form;
const { TabPane } = Tabs;

const Container = styled(Space)`
  width: 100%;
`;

type SurgeryEnquiryFormState = SurgeryEnquiry & {
  attachments: Attachments;
};

function SurgeryEnquiryPage() {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const { keycloak } = useKeycloak();
  const [formData, setFormData] = useState<SurgeryEnquiry>({});
  const [attachments, setAttachments] = useState<Attachments>();
  const [enquiryReference, setEnquiryReference] = useState<string>('');
  const [finished, setFinished] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    shouldFetch: shouldPostEnquirySubmission,
    data: surgeryEnquirySubmissionData,
    isLoading: isSubmissionLoading,
  } = useSurgeryEnquirySubmission(
    { key: 'surgeryEnquirySubmission', surgery: formData.surgery, diagnosis: formData.diagnosis, uploadFlag: '' },
    keycloak.token || '',
    {
      surgery: formData.surgery,
      diagnosis: formData.diagnosis,
      uploadFlag: attachments && attachments.fileList.length > 0 ? true : false,
    },
    { shouldRetryOnError: false },
  );

  useEffect(() => {
    revalidateAntdFormLanguageChanged(form);
  }, [i18n.language, form]);

  const { shouldFetch: shouldPostEnquiryEmailSubmission } = useEnquiryEmailSubmission(
    { key: 'enquiryEmailSubmission', enquiryReference },
    keycloak.token ?? '',
    enquiryReference,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const handleSubmitForm = (values: SurgeryEnquiryFormState) => {
    setFormData({
      surgery: values.surgery,
      diagnosis: values.diagnosis,
    });

    setAttachments(values.attachments);
    shouldPostEnquirySubmission(true);
    setIsLoading(true);
  };

  useEffect(() => {
    if (surgeryEnquirySubmissionData) {
      const enquiryReference: string = surgeryEnquirySubmissionData.message;
      setEnquiryReference(enquiryReference);
      shouldPostEnquirySubmission(false);
    }
  }, [surgeryEnquirySubmissionData, shouldPostEnquirySubmission]);

  useEffect(() => {
    const finalizeEnquirySubmission = () => {
      // shouldPostEnquiryEmailSubmission(true);
      setFinished(true);
    };

    if (!enquiryReference) return;

    shouldPostEnquirySubmission(false);

    if (!attachments?.fileList?.length) {
      finalizeEnquirySubmission();
      setIsLoading(false);
      return;
    }

    uploadFiles({
      accessToken: keycloak.token ?? '',
      params: {
        // Note(Andy Chow): Check https://bitbucket.org/generali-hk/all-in-one-web-app/pull-requests/899 for below documents logic
        documents: attachments.fileList.map((f) => {
          return f.originFileObj ?? (f as RcFile);
        }),
        referenceNo: enquiryReference,
        enquiryFlag: 'SURGERYFLAG',
      },
    })
      .then(() => {
        finalizeEnquirySubmission();
      })
      .catch(() => {
        message[MessageType.ERROR](t('common.error.generic'));
      })
      .finally(() => setIsLoading(false));

    return () => shouldPostEnquiryEmailSubmission(false);
  }, [
    enquiryReference,
    attachments,
    setFinished,
    setIsLoading,
    shouldPostEnquirySubmission,
    shouldPostEnquiryEmailSubmission,
    keycloak.token,
    t,
  ]);

  if (finished) {
    return <SurgeryEnquiryFinishResult enquiryReference={enquiryReference} />;
  }

  return (
    <Container direction="vertical" size="middle">
      <Space>
        <Image preview={false} src={NewEnquiryIcon} alt="surgery enquiry" />
        <Space direction="vertical">
          <Title level={2} style={{ marginBottom: 0 }}>
            {t('benefits.surgery.enquiry')}
          </Title>
          <Text>{t('benefits.surgery.enquiryDesc')}</Text>
        </Space>
      </Space>
      <Form form={form} requiredMark={false} layout="vertical" wrapperCol={{ span: 24 }} onFinish={handleSubmitForm}>
        <Row gutter={8}>
          <Col xs={24} md={18}>
            <Container direction="vertical">
              <EnquiryInformationCard />
              <SupplementaryDocumentCard />
              <FormItem>
                <PrimaryButton loading={isSubmissionLoading || isLoading} onClick={() => form.submit()}>
                  {t('common.submit')}
                </PrimaryButton>
              </FormItem>
            </Container>
          </Col>
          <Col xs={0} md={6}>
            <Tabs tabPosition="right">
              <TabPane key="enquiryInformation" tab={`1. ${t('benefits.surgery.enquiryInformation')}`} />
              <TabPane key="supplementaryDocuments" tab={`2. ${t('benefits.surgery.attachmentTitle')}`} />
            </Tabs>
          </Col>
        </Row>
      </Form>
    </Container>
  );
}

export default SurgeryEnquiryPage;
